import { FormEvent, useState } from "react";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daio1bjp14a1h3mr77u0";

export default function SubscribeBlock() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <section
      id="subscribe"
      className="w-full px-4 md:px-8 py-12 md:py-16 bg-secondary-50 scroll-mt-24"
    >
      <div className="max-w-content mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center rounded-lg border border-secondary-200 bg-background-50 p-6 md:p-10">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.2em] text-secondary-700 uppercase">
              <span className="w-4 h-px bg-secondary-500"></span>
              Subscribe
            </span>
            <h2 className="mt-3 font-heading font-black text-2xl md:text-3xl text-foreground-950">
              订阅新瓜早报
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              每天早上八点，把昨夜到今晨的新鲜瓜整理成一份简短清单发给你。只发内容，不打扰。
            </p>
            <ul className="mt-5 space-y-2 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-secondary-600"></i>
                每日一条，三分钟读完
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-secondary-600"></i>
                可按兴趣栏目订阅
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-secondary-600"></i>
                随时可以退订
              </li>
            </ul>
          </div>

          <form
            data-readdy-form
            id="subscribe-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <label htmlFor="subscribe-email" className="block text-sm text-foreground-700 mb-1.5">
              接收邮箱
            </label>
            <input
              id="subscribe-email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />

            <label htmlFor="subscribe-interest" className="block text-sm text-foreground-700 mt-4 mb-1.5">
              最关注的瓜田
            </label>
            <select
              id="subscribe-interest"
              name="interest"
              className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              <option value="">全部栏目</option>
              {categories.map((cat) => (
                <option key={cat.slug} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>

            <input
              className="form-guard-field"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-5 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {status === "loading" ? "正在提交…" : "订阅新瓜早报"}
            </button>

            {status === "success" && (
              <p className="mt-3 text-sm text-secondary-700 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-fill"></i>
                订阅成功，谢谢你的信任，明天早上见！
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-primary-700 flex items-center gap-1.5">
                <i className="ri-error-warning-fill"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}